"use client";

import { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { Icon } from "@/components/ui/Icon";
import { createPortal } from "react-dom";
import type { Vehicle } from "@/data/vehicles";
import type { Locale } from "@/config/i18n";
import { LOCATION_BOOKING_FORM_ID } from "../../components/locationBooking";
import {
  VEHICLES_UI,
  localizeFuel,
  localizeGearbox,
  vehiclesBasePath,
} from "../../components/vehiclesUi";
import { FICHE_UI } from "./ficheUi";
import { tr } from "@/config/localeText";
import {
  formatEuro,
  getRentalPriceTiers,
  RENTAL_DURATION_OPTIONS,
} from "../../components/pricing";

// Composants internes
const BackLink = ({ locale }: { locale: Locale }) => (
  <Link
    href={vehiclesBasePath(locale)}
    className="text-sm text-[#3b0e0e]/70 underline underline-offset-4 transition-colors hover:text-[#3b0e0e]"
  >
    {tr(FICHE_UI, locale).presentation.back}
  </Link>
);

const PageTitle = ({ title, make }: { title: string; make: string }) => (
  <div className="text-center">
    <h1 className="text-2xl font-semibold text-[#3b0e0e] sm:text-3xl lg:text-4xl">
      {title}
    </h1>
    <p className="mt-2 text-primaryLoc/80">{make}</p>
  </div>
);

const Thumb = ({
  src,
  alt,
  onClick,
  isActive = false,
  title,
}: {
  src: string;
  alt: string;
  onClick: () => void;
  isActive?: boolean;
  title: string;
}) => (
  <button
    title={title}
    type="button"
    onClick={onClick}
    className={`group relative aspect-[4/3] w-full cursor-pointer overflow-hidden rounded-lg focus:outline-none ${
      isActive ? "ring-2 ring-[#3b0e0e]" : ""
    }`}
  >
    <Image
      src={src}
      alt={alt}
      fill
      className="object-cover transition-transform group-hover:scale-105"
    />
  </button>
);

const AllPhotosCtaThumb = ({
  src,
  count,
  onClick,
  locale,
}: {
  src: string;
  count: number;
  onClick: () => void;
  locale: Locale;
}) => {
  const t = tr(FICHE_UI, locale).presentation;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative aspect-[4/3] w-full cursor-pointer overflow-hidden rounded-lg focus:outline-none"
      aria-label={t.openGallery}
    >
      <Image
        src={src}
        alt={t.allPhotos}
        fill
        className="object-cover transition-transform group-hover:scale-105"
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/45 text-center transition-colors group-hover:bg-black/55">
        <div>
          <Icon
            icon="material-symbols:photo-library-outline-rounded"
            className="mx-auto mb-2 h-8 w-8 text-white"
          />
          <p className="text-sm font-medium text-white">
            {t.allPhotos}
            <br />
            <span className="text-xs opacity-80">{t.photosCount(count)}</span>
          </p>
        </div>
      </div>
    </button>
  );
};

const MediaGrid = ({
  mainImage,
  thumbs,
  activeIndex,
  onThumbClick,
  onMainImageClick,
  onAllPhotosClick,
  locale,
}: {
  mainImage: string;
  thumbs: string[];
  activeIndex: number;
  onThumbClick: (index: number) => void;
  onMainImageClick: () => void;
  onAllPhotosClick: () => void;
  locale: Locale;
}) => {
  const t = tr(FICHE_UI, locale).presentation;
  return (
    <div className="grid gap-4 md:gap-6 lg:grid-cols-12">
      {/* Image principale */}
      <div className="lg:col-span-8">
        <button
          title={t.seeMainPhoto}
          type="button"
          onClick={onMainImageClick}
          className="relative aspect-[4/3] w-full cursor-zoom-in overflow-hidden rounded-lg focus:outline-none"
        >
          <Image
            src={mainImage}
            alt={t.mainView}
            fill
            className="object-cover"
            priority
          />
        </button>
      </div>

      {/* Miniatures */}
      <div className="grid grid-cols-2 gap-4 lg:col-span-4 lg:grid-cols-1">
        {thumbs[1] ? (
          <Thumb
            src={thumbs[1]}
            alt={`${t.altView} 1`}
            title={t.seePhoto}
            onClick={() => onThumbClick(1)}
            isActive={activeIndex === 1}
          />
        ) : (
          <Thumb
            src={thumbs[0]}
            alt={t.mainView}
            title={t.seePhoto}
            onClick={() => onThumbClick(0)}
            isActive={activeIndex === 0}
          />
        )}
        <AllPhotosCtaThumb
          src={thumbs[2] ?? thumbs[0]}
          count={thumbs.length}
          onClick={onAllPhotosClick}
          locale={locale}
        />
      </div>
    </div>
  );
};

const SpecBar = ({ vehicle, locale }: { vehicle: Vehicle; locale: Locale }) => {
  const t = tr(FICHE_UI, locale).presentation;
  return (
    <div className="mt-8 grid grid-cols-2 gap-4 border-y border-primaryLoc/15 py-6 sm:grid-cols-4 sm:gap-6">
      <div className="text-center">
        <p className="text-lg font-medium text-[#3b0e0e]">
          {vehicle.specs.year}
        </p>
        <p className="mt-1 text-xs text-primaryLoc/60">{t.year}</p>
      </div>
      <div className="text-center">
        <p className="text-lg font-medium text-[#3b0e0e]">
          {vehicle.specs.mileage.toLocaleString()} km
        </p>
        <p className="mt-1 text-xs text-primaryLoc/60">{t.mileage}</p>
      </div>
      <div className="text-center">
        <p className="text-lg font-medium text-[#3b0e0e]">
          {localizeGearbox(vehicle.specs.gearbox, locale)}
        </p>
        <p className="mt-1 text-xs text-primaryLoc/60">{t.gearbox}</p>
      </div>
      <div className="text-center">
        <p className="text-lg font-medium text-[#3b0e0e]">
          {localizeFuel(vehicle.specs.fuel, locale)}
        </p>
        <p className="mt-1 text-xs text-primaryLoc/60">{t.fuel}</p>
      </div>
    </div>
  );
};

const PriceCtaBar = ({
  vehicle,
  locale,
}: {
  vehicle: Vehicle;
  locale: Locale;
}) => {
  const t = tr(FICHE_UI, locale).presentation;
  const durationLabels = tr(VEHICLES_UI, locale).durationLabels;
  const rentalTiers = getRentalPriceTiers(vehicle);

  return (
    <div className="flex flex-col items-start justify-between gap-5 py-6 sm:flex-row sm:items-center">
      <div className="space-y-2">
        <p className="text-sm text-primaryLoc/80">{t.indicativeRate}</p>
        {vehicle.price.km_limit && (
          <p className="text-xs text-primaryLoc/65">
            {t.kmPerDay(vehicle.price.km_limit)}
          </p>
        )}
      </div>
      <div className="flex w-full flex-wrap items-center justify-between gap-4 sm:w-auto">
        {rentalTiers ? (
          <div className="grid min-w-[19rem] grid-cols-3 gap-2">
            {RENTAL_DURATION_OPTIONS.map((option) => (
              <div
                key={option.value}
                className="rounded-lg border border-primaryLoc/15 bg-secondary/35 px-2.5 py-2 text-center"
              >
                <p className="text-[10px] uppercase tracking-[0.03em] text-primaryLoc/70">
                  {durationLabels[option.value]}
                </p>
                <p className="text-sm font-semibold text-primaryLoc">
                  {formatEuro(rentalTiers[option.value])}
                  {t.perDay}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-3xl font-semibold text-[#3b0e0e] lg:text-4xl">
            {formatEuro(vehicle.price.amount)}
          </p>
        )}
        <Link
          href={`#${LOCATION_BOOKING_FORM_ID}`}
          className="inline-flex items-center justify-center rounded-full border border-[#3b0e0e] px-5 py-2 text-sm font-medium text-[#3b0e0e] transition-colors hover:bg-[#3b0e0e] hover:text-white focus-visible:ring-2 focus-visible:ring-[#3b0e0e]"
        >
          {t.order}
        </Link>
      </div>
    </div>
  );
};
const MediaLightbox = ({
  isOpen,
  onClose,
  images,
  currentIndex,
  onIndexChange,
  locale,
}: {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  locale: Locale;
}) => {
  const t = tr(FICHE_UI, locale).presentation;
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft")
        onIndexChange((currentIndex - 1 + images.length) % images.length);
      if (e.key === "ArrowRight")
        onIndexChange((currentIndex + 1) % images.length);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, currentIndex, images.length, onClose, onIndexChange]);

  if (!isOpen) return null;

  const goToPrevious = () =>
    onIndexChange((currentIndex - 1 + images.length) % images.length);
  const goToNext = () => onIndexChange((currentIndex + 1) % images.length);

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-black/95"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative mx-auto flex h-full w-full max-w-7xl flex-col px-4 py-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-30 rounded-full bg-black/45 p-2 text-white/90 transition-colors hover:text-white"
          aria-label={t.closeGallery}
        >
          <Icon icon="mdi:close" className="h-6 w-6" />
        </button>

        <div className="absolute right-16 top-5 z-20 rounded bg-black/45 px-3 py-1 text-sm text-white">
          {currentIndex + 1} / {images.length}
        </div>

        <div className="relative flex min-h-0 flex-1 items-center justify-center">
          <div className="relative aspect-[16/10] w-full max-w-6xl overflow-hidden rounded-xl">
            <Image
              src={images[currentIndex]}
              alt={t.photoOf(currentIndex + 1, images.length)}
              fill
              className="object-contain"
              sizes="100vw"
              priority
            />
          </div>
        </div>

        <button
          title={t.prevPhoto}
          type="button"
          onClick={goToPrevious}
          className="absolute left-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/45 p-1 text-white/85 transition-colors hover:text-white md:left-6"
        >
          <Icon icon="mdi:chevron-left" className="h-8 w-8 md:h-10 md:w-10" />
        </button>
        <button
          title={t.nextPhoto}
          type="button"
          onClick={goToNext}
          className="absolute right-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/45 p-1 text-white/85 transition-colors hover:text-white md:right-6"
        >
          <Icon icon="mdi:chevron-right" className="h-8 w-8 md:h-10 md:w-10" />
        </button>

        {images.length > 1 ? (
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
            {images.map((src, index) => (
              <button
                key={`${src}-${index}`}
                type="button"
                onClick={() => onIndexChange(index)}
                className={`relative h-14 w-24 shrink-0 overflow-hidden rounded-md border transition ${
                  currentIndex === index
                    ? "border-white"
                    : "border-white/25 hover:border-white/60"
                }`}
                aria-label={t.goToPhoto(index + 1)}
              >
                <Image
                  src={src}
                  alt={t.thumb(index + 1)}
                  fill
                  className="object-cover"
                  sizes="96px"
                />
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};

// Composant principal
export default function PresentationCar({
  vehicle,
  locale = "fr",
}: {
  vehicle: Vehicle;
  locale?: Locale;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = useMemo(() => {
    const ordered = [vehicle.images.main, ...vehicle.images.gallery].map(
      (img) => img.src,
    );
    return Array.from(new Set(ordered));
  }, [vehicle.images.main, vehicle.images.gallery]);

  const safeActiveIndex =
    images.length > 0
      ? Math.min(Math.max(activeIndex, 0), images.length - 1)
      : 0;

  return (
    <main className="min-h-screen bg-white py-20 md:py-24">
      <section className="mx-auto max-w-[90rem] px-6 py-8 lg:px-8 lg:py-12">
        <BackLink locale={locale} />
        <div className="mb-8 mt-5 md:mb-10 md:mt-6">
          <PageTitle title={vehicle.name} make={vehicle.brand} />
        </div>
        <MediaGrid
          mainImage={images[safeActiveIndex]}
          thumbs={images}
          activeIndex={safeActiveIndex}
          onThumbClick={setActiveIndex}
          onMainImageClick={() => setLightboxOpen(true)}
          onAllPhotosClick={() => setLightboxOpen(true)}
          locale={locale}
        />
        <SpecBar vehicle={vehicle} locale={locale} />
        <PriceCtaBar vehicle={vehicle} locale={locale} />
      </section>

      <MediaLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={images}
        currentIndex={safeActiveIndex}
        onIndexChange={setActiveIndex}
        locale={locale}
      />
    </main>
  );
}
